"use client";
import Link from "next/link";
import { useEffect, useState } from "react";

export default function TicketPage() {
  const [webContent, setWebContent] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [parkTiming, setParkTiming] = useState(null);
  const [activeOffer, setActiveOffer] = useState(null);
  const [ticketSettings, setTicketSettings] = useState(null);

  useEffect(() => {
    const fetchWebContent = async () => {
      try {
        const response = await fetch("/api/web-content");
        const data = await response.json();
        setWebContent(data);

        const contactResponse = await fetch("/api/contactus");
        const contactDataResult = await contactResponse.json();
        if (contactDataResult && contactDataResult.length > 0) {
          setContactData(contactDataResult[0]);
        }
        const parkTimingRes = await fetch("/api/parktiming");
        const parkTimingData = await parkTimingRes.json();
        if (Array.isArray(parkTimingData) && parkTimingData.length > 0) {
          setParkTiming(parkTimingData[0]);
        }
        const offerRes = await fetch("/api/offers/active");
        const offerData = await offerRes.json();
        if (offerData.success && offerData.hasActiveOffers && offerData.primaryOffer) {
          setActiveOffer(offerData.primaryOffer);
        } else {
          setActiveOffer(null);
        }

        const settingsRes = await fetch("/api/ticketsettings");
        const settingsData = await settingsRes.json();
        if (settingsData && settingsData.ticketSettings) {
          setTicketSettings(settingsData.ticketSettings);
        }
      } catch (error) {
        console.error("Error fetching web content:", error);
      }
    };
    fetchWebContent();
  }, []);

  return (
    <>
      {/* Breadcrumb Header */}
      <div
        className="breadcrumb-wrapper bg-cover"
        style={{ backgroundImage: 'url("/assets/img/booktickets.jpeg")' }}
      >
        <div className="container">
          <div className="breadcrumb-wrapper-items">
            <div className="page-heading">
              <div className="breadcrumb-sub-title">
                <h1 className="wow fadeInUp" data-wow-delay=".3s">
                  Ticket Booking
                </h1>
              </div>
              <ul
                className="breadcrumb-items wow fadeInUp"
                data-wow-delay=".5s"
              >
                <li>
                  <Link href="/"> Home </Link>
                </li>
                <li>
                  <i className="fa-sharp fa-solid fa-slash-forward" />
                </li>
                <li>Ticket</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <section
        className="ticket-section py-4 py-md-5"
        style={{ background: "linear-gradient(180deg, #f4fbff 0%, #ffffff 100%)", minHeight: "80vh" }}
      >
        <div className="container">
          <div className="d-flex flex-column gap-4 max-w-6xl mx-auto">
            {/* Top Row: 2 Category Cards */}
            <div className="row g-4">
              {/* Card 1: Standard Tickets */}
              <div className={activeOffer ? "col-md-6" : "col-12"}>
                <div
                  className="p-4 rounded-4 h-100 shadow-sm transition-all"
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1.5px solid #d0f0fd",
                    boxShadow: "0 4px 20px rgba(2, 132, 199, 0.06)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(2, 132, 199, 0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(2, 132, 199, 0.06)";
                  }}
                >
                  <div className={`d-flex ${activeOffer ? "flex-column flex-lg-row align-items-lg-center" : "flex-column flex-md-row align-items-md-center"} justify-content-between gap-4`}>
                    <div className="d-flex align-items-center gap-4">
                      {/* Blue Icon */}
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: "68px",
                          height: "68px",
                          backgroundColor: "#e0f4fc",
                          color: "#0284c7",
                          border: "1.5px solid #bae6fd"
                        }}
                      >
                        <i className="fa-solid fa-ticket fs-3" style={{ transform: "rotate(-25deg)" }}></i>
                      </div>

                      {/* Content */}
                      <div>
                        <h3
                          className="fw-bold text-dark mb-1"
                          style={{ fontSize: "1.45rem", letterSpacing: "-0.3px" }}
                        >
                          {ticketSettings?.standardTicketTitle || "Standard Tickets"}
                        </h3>
                        <p className="text-secondary mb-0" style={{ fontSize: "0.95rem", lineHeight: "1.45" }}>
                          {ticketSettings?.standardTicketDescription || webContent?.standardTicketDescription || "Entry to all rides & pools on your selected date of visit."}
                        </p>
                      </div>
                    </div>

                    {/* Button on the right side */}
                    <div className="flex-shrink-0 ms-auto ms-md-0">
                      <Link
                        href="/ticket/standard"
                        className="btn text-white rounded-pill px-4 py-2.5 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm text-nowrap"
                        style={{ backgroundColor: "#0084cc", fontSize: "0.95rem" }}
                      >
                        Book tickets <i className="fa-solid fa-arrow-right small"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Special Offers (Only rendered if an offer is ACTIVE and date is valid) */}
              {activeOffer && (
                <div className="col-md-6">
                  <div
                    className="p-4 rounded-4 h-100 shadow-sm transition-all"
                    style={{
                      backgroundColor: "#fffaf7",
                      border: "1.5px solid #fed7aa",
                      boxShadow: "0 4px 20px rgba(249, 85, 0, 0.06)",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(249, 85, 0, 0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 20px rgba(249, 85, 0, 0.06)";
                    }}
                  >
                    <div className={`d-flex ${activeOffer ? "flex-column flex-lg-row align-items-lg-center" : "flex-column flex-md-row align-items-md-center"} justify-content-between gap-4`}>
                      <div className="d-flex align-items-center gap-4">
                        {/* Orange Icon */}
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: "68px",
                            height: "68px",
                            backgroundColor: "#ffedd5",
                            color: "#f95500",
                            border: "1.5px solid #fed7aa"
                          }}
                        >
                          <i className="fa-solid fa-gift fs-3"></i>
                        </div>

                        {/* Content */}
                        <div>
                          <h3
                            className="fw-bold text-dark mb-1"
                            style={{ fontSize: "1.45rem", letterSpacing: "-0.3px" }}
                          >
                            {ticketSettings?.specialOffersTitle || "Special Offers"}
                          </h3>
                          <p className="text-secondary mb-0" style={{ fontSize: "0.95rem", lineHeight: "1.45" }}>
                            {ticketSettings?.specialOffersDescription || activeOffer?.description || webContent?.specialOffersDescription || "Explore exclusive promo deals & bundle savings on your visit."}
                          </p>
                        </div>
                      </div>

                      {/* Button on the right side */}
                      <div className="flex-shrink-0 ms-auto ms-md-0">
                        <Link
                          href="/ticket/offers"
                          className="btn text-white rounded-pill px-4 py-2.5 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm text-nowrap"
                          style={{ backgroundColor: "#f95500", fontSize: "0.95rem" }}
                        >
                          Explore offers <i className="fa-solid fa-arrow-right small"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Row: Rescheduling Policy & Park Timings */}
            <div className="row g-4 align-items-stretch">
              {/* Left Column: Rescheduling Policy */}
              <div className="col-lg-7">
                <div
                  className="p-4 rounded-4 bg-white shadow-sm h-100 d-flex flex-column justify-content-between"
                  style={{ border: "1.5px solid #c2edfc" }}
                >
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                      <i className="fa-solid fa-triangle-exclamation" style={{ color: "#f95500", fontSize: "1.25rem" }}></i>
                      <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.25rem" }}>
                        Rescheduling policy
                      </h4>
                    </div>

                    <div className="d-flex flex-column">
                      <div className="d-flex gap-3 align-items-center py-3 border-bottom">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: "40px", height: "40px", backgroundColor: "#fff0e6", color: "#f95500" }}
                        >
                          <i className="fa-solid fa-arrows-rotate fs-6"></i>
                        </div>
                        <div className="text-dark" style={{ fontSize: "0.93rem" }}>
                          Date changes are allowed once, subject to availability.
                        </div>
                      </div>

                      <div className="d-flex gap-3 align-items-center py-3 border-bottom">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: "40px", height: "40px", backgroundColor: "#fff0e6", color: "#f95500" }}
                        >
                          <i className="fa-regular fa-calendar-xmark fs-6"></i>
                        </div>
                        <div className="text-dark" style={{ fontSize: "0.93rem" }}>
                          Same-day bookings cannot be rescheduled.
                        </div>
                      </div>

                      <div className="d-flex gap-3 align-items-center pt-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: "40px", height: "40px", backgroundColor: "#fff0e6", color: "#f95500" }}
                        >
                          <i className="fa-regular fa-clock fs-6"></i>
                        </div>
                        <div className="text-dark" style={{ fontSize: "0.93rem" }}>
                          For future-date bookings, reschedule before 12:00 AM on the previous day.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Park Timings */}
              <div className="col-lg-5">
                <div
                  className="p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-between"
                  style={{ backgroundColor: "#f0f9ff", border: "1.5px solid #bae6fd" }}
                >
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom" style={{ borderColor: "#bae6fd" }}>
                      <i className="fa-regular fa-clock" style={{ color: "#0284c7", fontSize: "1.25rem" }}></i>
                      <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.25rem" }}>
                        Park timings
                      </h4>
                    </div>

                    <div className="d-flex flex-column gap-3 pt-1">
                      <div className="d-flex gap-3 align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: "42px", height: "42px", backgroundColor: "#e0f2fe", color: "#0284c7" }}
                        >
                          <i className="fa-regular fa-calendar-days fs-6"></i>
                        </div>
                        <div>
                          <div className="text-dark fw-bold" style={{ fontSize: "0.95rem" }}>Weekdays</div>
                          <div className="text-secondary" style={{ fontSize: "0.88rem" }}>
                            {parkTiming ? `${parkTiming.weekdayOpeningTime} to ${parkTiming.weekdayClosingTime}` : "10:00 to 17:00"}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex gap-3 align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: "42px", height: "42px", backgroundColor: "#e0f2fe", color: "#0284c7" }}
                        >
                          <i className="fa-regular fa-calendar-days fs-6"></i>
                        </div>
                        <div>
                          <div className="text-dark fw-bold" style={{ fontSize: "0.95rem" }}>Weekends</div>
                          <div className="text-secondary" style={{ fontSize: "0.88rem" }}>
                            {parkTiming ? `${parkTiming.weekendOpeningTime} to ${parkTiming.weekendClosingTime}` : "10:00 to 19:00"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
